<script setup lang="ts">
import { computed, h, type PropType, type VNodeChild } from 'vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import { IconAlert } from '@/components/design/icons';
import { parseGaiaLink } from '@/utils/chat/gaiaLinks';
import GaiaAvatar from '../../base/GaiaAvatar.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaChatReference from './GaiaChatReference.vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
  gaiaTracking,
} from '../../themes/tokens';

const props = defineProps({
  /** user = right-aligned bubble; assistant = mark avatar + flowing answer. */
  sender: {
    type: String as PropType<'user' | 'assistant'>,
    default: 'assistant',
  },
  /** Assistant only: show the streaming caret after the text. */
  streaming: {
    type: Boolean,
    default: false,
  },
  /** Assistant only: show the pulsing pre-answer line (#pending slot) instead of text. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Assistant only: the run failed and the default slot is the failure copy,
   * not an answer — set in the alert tone so it never reads as one.
   */
  failed: {
    type: Boolean,
    default: false,
  },
  /** User avatar initials (assistant always shows the G mark). */
  initials: {
    type: String,
    default: '',
  },
  /**
   * Assistant answers arrive as markdown. While the run is still streaming the
   * text is rendered raw — a half-written table or an unclosed fence reflows on
   * every token, and re-lexing the whole answer per token is wasteful. It
   * switches to rendered markdown the moment the run settles.
   */
  markdown: {
    type: Boolean,
    default: false,
  },
  /** Markdown source. Only read when `markdown` is set; otherwise use the default slot. */
  content: {
    type: String,
    default: '',
  },
});

const emit = defineEmits<{
  /** A reference chip in the answer was clicked; the path is ready to push. */
  'open-reference': [path: string];
}>();

/** The agent's `gaia://` links become reference links; every other link renders as usual. */
function renderLink(href: string, _children: VNodeChild[], text: string): VNodeChild | null {
  const link = parseGaiaLink(href);
  if (!link) return null;
  return h(GaiaChatReference, {
    kind: link.kind,
    path: link.path,
    name: text,
    onOpen: (path: string) => {
      emit('open-reference', path);
    },
  });
}

/**
 * Before the first token the body renders nothing, so the status block is the
 * column's first line and has to sit on the avatar beside it — the gap that
 * separates it from a written answer would only push it below the mark.
 */
const statusLeadsColumn = computed(
  () => props.markdown && !props.pending && props.content.length === 0
);
</script>

<template>
  <n-flex v-if="props.sender === 'user'" align="flex-start" justify="end" :size="12" :wrap="false">
    <n-flex vertical align="flex-end" :size="8" :style="{ minWidth: 0, maxWidth: '78%' }">
      <gaia-text class="bubble" tone="ink">
        <slot />
      </gaia-text>
      <!-- Files this turn referenced, under the bubble and right-aligned with it. -->
      <n-flex v-if="$slots.attachments" class="attachments" justify="flex-end" :size="6">
        <slot name="attachments" />
      </n-flex>
    </n-flex>
    <span class="avatar">
      <slot name="avatar">
        <gaia-avatar :size="26">{{ props.initials }}</gaia-avatar>
      </slot>
    </span>
  </n-flex>
  <n-flex v-else align="flex-start" :size="12" :wrap="false">
    <span class="avatar">
      <slot name="avatar">
        <gaia-avatar variant="mark" :size="26">G</gaia-avatar>
      </slot>
    </span>
    <!-- 4px pad centers the 26px mark on the first ~22px text line. -->
    <div :style="{ minWidth: 0, flex: 1, paddingTop: '4px' }">
      <n-flex v-if="props.pending" align="center" :size="8" :wrap="false">
        <span class="pending-dot" />
        <gaia-text tone="faint"><slot name="pending">Working…</slot></gaia-text>
      </n-flex>
      <n-flex v-else-if="props.failed" align="flex-start" :size="6" :wrap="false">
        <!-- 3px pad centers the 16px glyph on the first text line. -->
        <n-icon
          :component="IconAlert"
          :size="16"
          :color="gaiaStatus.alert"
          :style="{ flex: 'none', marginTop: '3px' }"
        />
        <gaia-text tone="alert" :style="{ display: 'block', lineHeight: 1.62 }">
          <slot />
        </gaia-text>
      </n-flex>
      <div v-else-if="props.markdown && !props.streaming" class="markdown">
        <slot name="markdown">
          <markdown-renderer :content="props.content" :render-link="renderLink" />
        </slot>
      </div>
      <gaia-text
        v-else
        tone="ink"
        :style="{ display: 'block', lineHeight: 1.62, whiteSpace: 'pre-wrap' }"
      >
        <slot /><span v-if="props.streaming" :style="{ color: gaiaNeutral.textFaint }">▍</span>
      </gaia-text>
      <!-- What the run is doing, under whatever it has written so far. -->
      <div v-if="$slots.status" :style="{ marginTop: statusLeadsColumn ? '0' : '10px' }">
        <slot name="status" />
      </div>
      <div v-if="$slots.interrupt" :style="{ marginTop: '14px' }">
        <slot name="interrupt" />
      </div>
      <n-flex v-if="$slots.footer" align="center" :size="14" :wrap="false" class="msg-footer">
        <slot name="footer" />
      </n-flex>
    </div>
  </n-flex>
</template>

<style scoped>
/* Chat thread layout from mock 5a. Naive has no chat/message component, so
   the bubble, pending line and sources footer are our own elements; all
   colors come from the shared tokens. */
.avatar {
  flex: none;
  margin-top: 2px;
}

.bubble {
  display: block;
  background: v-bind('gaiaNeutral.surfaceTint');
  border: 1px solid v-bind('gaiaNeutral.borderMuted');
  border-radius: 14px 14px 4px 14px;
  padding: 12px 16px;
  line-height: 1.5;
  font-weight: v-bind('String(gaiaFontWeight.medium)');
  letter-spacing: v-bind('gaiaTracking.snug');
  /* The question is echoed as typed: pasted tables and multi-line prompts keep
     their line breaks and tabs instead of collapsing into one run of text. */
  white-space: pre-wrap;
}

/* A file name is one unbreakable run and n-tag never wraps, so a long one
   sizes this row past the bubble's 78% and out of the thread. Each chip
   ellipsizes at the bubble's width instead; `overflow: hidden` on the content
   is also what lets it shrink at all (it zeroes the flex item's auto min). */
.attachments {
  max-width: 100%;
}

.attachments :deep(.n-tag) {
  max-width: 100%;
}

.attachments :deep(.n-tag__content) {
  overflow: hidden;
  text-overflow: ellipsis;
}

.pending-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: v-bind('gaiaNeutral.textFaint');
  flex: none;
  /* The one signal that a run is alive before any token arrives. */
  animation: gaia-chat-pending 1.4s ease-in-out infinite;
}

@keyframes gaia-chat-pending {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}

.msg-footer {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid v-bind('gaiaNeutral.borderSubtle');
  color: v-bind('gaiaNeutral.textMuted');
}

/* MarkdownRenderer is shared with the legacy views and hardcodes its own
   palette (#2c3e50 headings, #f5f5f5 code, #2080f0 links). Re-tinting it onto
   the Gaia tokens here keeps that component untouched for its other consumers
   — this is the one surface that needs it to look like the design system. */
.markdown {
  line-height: 1.62;
}

/* The renderer gives every block a top margin; on the first one it only pushes
   the answer down past the avatar. */
.markdown :deep(.markdown-content > :first-child) {
  margin-top: 0;
}

.markdown :deep(h1),
.markdown :deep(h2),
.markdown :deep(h3),
.markdown :deep(h4),
.markdown :deep(h5),
.markdown :deep(h6) {
  color: v-bind('gaiaNeutral.ink');
  font-weight: v-bind('String(gaiaFontWeight.semibold)');
  letter-spacing: v-bind('gaiaTracking.snug');
}

.markdown :deep(p),
.markdown :deep(li),
.markdown :deep(td),
.markdown :deep(th) {
  color: v-bind('gaiaNeutral.ink');
}

.markdown :deep(a) {
  color: v-bind('gaiaAccent.strong');
}

.markdown :deep(code) {
  background: v-bind('gaiaNeutral.surfaceTint');
  color: v-bind('gaiaNeutral.ink');
  border-radius: 4px;
}

.markdown :deep(pre) {
  background: v-bind('gaiaNeutral.surfaceTint');
  border: 1px solid v-bind('gaiaNeutral.borderMuted');
  border-radius: 10px;
}

.markdown :deep(blockquote) {
  border-left: 2px solid v-bind('gaiaNeutral.borderDivider');
  color: v-bind('gaiaNeutral.textTertiary');
}

.markdown :deep(hr) {
  border-color: v-bind('gaiaNeutral.borderSubtle');
}

.markdown :deep(table) {
  font-size: v-bind('gaiaFontSize.body');
}
</style>
